import type { GridSortItem, GridSortModel } from '@mui/x-data-grid-pro';
import camelCase from 'lodash.camelcase';
import snakeCase from 'lodash.snakecase';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Converts a GridSortItem to something like "IP_ADDRESS_DESC"
export const toOrderByParam = (item: GridSortItem | null): string | null => {
  if (!item || !item.field || !item.sort) return null;
  return `${snakeCase(item.field)}_${item.sort}`.toUpperCase();
};

// Converts "IP_ADDRESS_DESC" to a GridSortItem[]
export const fromOrderByParam = (param: string | null): GridSortItem[] => {
  if (!param) return [];

  const parts = param.split('_');
  if (parts.length < 2) return [];

  const field = camelCase(parts.slice(0, -1).join('_'));
  const sort = parts.at(-1)?.toLowerCase() === 'desc' ? 'desc' : 'asc';

  return [{ field, sort }];
};

// Converts a GridSortItem to URL-encoded format (for data sources)
const toRawOrderByParam = (item: GridSortItem | null): string | null => {
  if (!item || !item.field || !item.sort) return null;
  return `${item.field}_${item.sort.toUpperCase()}`;
};

// Converts URL-encoded param back to GridSortItem
const fromRawOrderByParam = (param: string | null): GridSortItem[] => {
  if (!param) return [];

  const match = param.match(/^(.+)_(ASC|DESC)$/i);
  if (!match) return [];

  const field = match[1];
  const sort = match[2].toLowerCase() as 'asc' | 'desc';

  return [{ field, sort }];
};

// Hook for URL-based usage
export const useOrderByParams = <T extends string>(type: 'standard' | 'dynamic' = 'standard') => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('orderBy');

  const setOrderByParams = useCallback(
    (item: GridSortItem | null) => {
      const param = type === 'standard' ? toOrderByParam(item) : toRawOrderByParam(item);
      setSearchParams((params) => {
        if (param) {
          params.set('orderBy', param);
        } else {
          params.delete('orderBy');
        }
        return params;
      });
    },
    [setSearchParams, type],
  );

  const sortModel = useMemo(
    () => (type === 'standard' ? fromOrderByParam(raw) : fromRawOrderByParam(raw)),
    [raw, type],
  );

  return {
    orderBy: raw ? (raw as T) : null,
    setOrderByParams,
    sortModel,
  };
};

// Non-URL helper for component-local state
export const useLocalOrderBy = <T extends string>(initial: GridSortModel) => {
  const [sortModel, setSortModel] = useState<GridSortModel>(initial);
  const orderBy = useMemo(() => toOrderByParam(sortModel[0]) as T, [sortModel]);
  return { orderBy, sortModel, setSortModel };
};
